import { useEffect, useRef, useState } from 'react';
import { LwButton } from '@/components/lw';
import { FeeAuditChain } from '@/components/encompass/prospects/appraisal/FeeAuditChain';
import { feeAuditChain, type FeeChain } from '@/components/encompass/prospects/appraisal/feeAuditChain';
import type { TrackedFeeAudit } from './AppraisalFeeCompliance';

export function auditProgress(chain: FeeChain, queued: boolean, finished: boolean): number {
  if (queued) {
    return 10;
  }
  if (finished && !chain.links.some(link => link.state === 'broken')) {
    return 100;
  }
  const [read, check, name] = chain.links;
  if (name.state === 'active' || name.state === 'broken') {
    return 92;
  }
  if (check.state === 'active' || check.state === 'broken') {
    return 80;
  }
  if (read.state === 'done') {
    return 55;
  }
  return 35;
}

/** Only the elapsed clock uses a timer. The three steps and progress use saved audit state. */
export function AppraisalAuditTracker({ run, onDismiss, onHistory }: {
  run: TrackedFeeAudit; onDismiss: () => void; onHistory: () => void;
}) {
  const chain = feeAuditChain(run.row);
  const finished = run.phase === 'settled' || run.phase === 'rejected';
  const stopped = run.phase === 'rejected' || chain.links.some(link => link.state === 'broken');
  const stoppedHeadline = `Audit stopped for ${run.initial.loanNumber}`;
  const headline = run.phase === 'queued' ? 'Queuing invoice audit…' : stopped || (finished && run.message?.startsWith(`${stoppedHeadline}. `)) ? stoppedHeadline
    : finished ? `Audit finished for ${run.initial.loanNumber}` : `Auditing ${run.initial.loanNumber}`;
  const message = run.message?.startsWith(`${headline}. `) ? run.message.slice(headline.length + 2) : run.message;
  const summary = message || (stopped ? 'Open History to see which step failed.' : chain.summary);
  const progress = auditProgress(chain, run.phase === 'queued' || run.phase === 'rejected', finished);
  const lastProgress = useRef(progress);
  const frozen = stopped ? lastProgress.current : progress;
  useEffect(() => {
    if (!stopped) {
      lastProgress.current = progress;
    }
  }, [progress, stopped]);
  const [elapsed, setElapsed] = useState(() => Math.max(0, Math.floor((Date.now() - run.startedAt) / 1000)));
  useEffect(() => {
    if (finished || stopped) {
      return;
    }
    const timer = window.setInterval(() => setElapsed(Math.max(0, Math.floor((Date.now() - run.startedAt) / 1000))), 1000);
    return () => window.clearInterval(timer);
  }, [finished, stopped, run.startedAt]);
  return <aside className="appraisal-audit-tracker" aria-label={`Invoice audit for ${run.initial.loanNumber}`} data-stopped={stopped} data-finished={finished}>
    <div className="appraisal-tracker-top"><span>Invoice audit</span><LwButton variant="sm" onClick={onDismiss}>{finished || stopped ? 'Close' : 'Run in background'}</LwButton></div>
    <h2>{headline}</h2>
    <p className="appraisal-tracker-title" title={run.row.documentTitle}>{run.row.documentTitle}</p>
    {(finished || stopped) && <p className="appraisal-tracker-summary">{summary}</p>}
    <progress aria-label="Invoice audit progress" max={100} value={frozen} />
    <FeeAuditChain chain={chain} compact ariaLabel={`Audit steps for ${run.row.documentTitle}`} />
    <div className="appraisal-tracker-footer"><span>{elapsed}s elapsed</span><LwButton variant="sm" onClick={onHistory}>Open History</LwButton></div>
  </aside>;
}
